import { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { WellMode } from '../../../types/routing-types'
import { Permissions, User } from '../../../types/user-types'
import { usePageState } from '../../context/page-context'
import WellStateProvider, { WellState } from '../../context/well-context'
import { useGetWellMetaData, useGetWellWorkflowState } from '../../hooks/use-api'
import useUser from '../../hooks/use-user'
import useWellPermissions from '../../hooks/use-well-permissions'
import {
  getExistingWellLinks,
  getNewWellLinks,
  getWellReviewLinks,
  WorkflowNavStatus,
} from '../../routes/utils/route-utils'
import { isValidToken } from '../../services/auth-utils'
import { can, canEditWell, isCouncilUserOrAdmin } from '../../services/user-utils'
import { UserActionType, UserContext } from '../../state/user-state'
import { ActionMenuContext } from '../page'
import AGSUploadModal from '../well-forms/ags-upload-modal'
import DeleteWellModal from '../well-forms/delete-well-modal'
import { useWellId } from '../../hooks/use-well-id'
import WorkflowStateProvider from '../../context/workflow-context'
import Cookies from 'js-cookie'
import { ExportSearchResultsToCsv, getFormattedDateTimeForExport } from '../../services/export-utils'

export interface WellEditInterface {
  mode: WellMode
}

// Could be more strongly typed
export interface WorkflowStateInterface {
  [step: string]: {
    [form: string]: WorkflowNavStatus
  }
}

function getHeaderTabs(id: string, mode: WellMode, user: User | null) {
  const headerTabs = [
    {
      label: 'Record',
      path: `/wells/${id}/${mode}/details`,
    },
  ]

  if (isValidToken(user?.token)) {
    if (isCouncilUserOrAdmin(user)) {
      const reviewTab = {
        label: 'Review',
        path: `/wells/${id}/edit/review`,
      }
      headerTabs.splice(1, 0, reviewTab)
      const historyTab = {
        label: 'History',
        path: `/wells/${id}/${mode}/history`,
        isNewSection: true,
      }
      headerTabs.splice(2, 0, historyTab)
    }
  }

  return headerTabs
}

export const WellMetaDataContext = createContext<{
  wellMetaData: WellState
  setWellMetaData: Dispatch<SetStateAction<WellState>>
}>(null!)

export default function WellPage({ mode }: WellEditInterface) {
  const {
    isAGSUploadModalOpen,
    setIsAGSUploadModalOpen,
    isDeleteWellModalOpen,
    setIsDeleteWellModalOpen,
    setShouldShowAGSLink,
    showStrataExport: shouldExportStrata,
    setShowStrataExport: setShouldExportStrata,
  } = useContext(ActionMenuContext)
  const id = useWellId()
  const { user } = useUser()
  const location = useLocation()
  const { dispatch } = useContext(UserContext)
  const [workflowState, setWorkflowState] = useState<WorkflowStateInterface>()
  const [wellMetaData, setWellMetaData] = useState<WellState>()
  const wellPermissions = useWellPermissions(id)
  const { setPageState } = usePageState()

  const { isSuccess: isWorkflowStateSuccess, data: workflowData } = useGetWellWorkflowState(
    `wells/${id}/workflow-state`,
    can(user, [Permissions.CREATE_WELL, Permissions.CREATE_WELL_UNSCOPED]) && id !== WellMode.NEW,
  )
  const { isSuccess: isWellMetaDataSuccess, data: metadataData } = useGetWellMetaData(`wells/${id}/meta-data`)

  const localDateTimeString = getFormattedDateTimeForExport()

  // Setup strata export query
  const strataExportQuery = JSON.stringify({
    searchQuery: {
      searchTypeKey: 'search-wells-national',
      viewKey: 'wellsStrataExport',
      requestedPage: 1,
      requestedPageLength: 1,
      filterSet: {
        booleanOperator: 'AND',
        filters: [
          {
            filterSet: {
              booleanOperator: 'OR',
              filters: [
                {
                  filterKey: 'wellId',
                  filterValue: id,
                  filterOperator: '=',
                },
              ],
            },
          },
        ],
      },
      ordering: [],
      requestedExport: true,
      exportFileName: `well-${id}-strata-export-${localDateTimeString}.csv`,
    },
  })

  const { requestCsvExportUrl } = ExportSearchResultsToCsv(
    `wells/csv-export`,
    Cookies.get('csrftoken'),
    strataExportQuery,
  )

  useEffect(() => {
    if (wellPermissions) {
      dispatch({ type: UserActionType.SET_USER_DETAILS, payload: { objectPermissions: wellPermissions } })
    }
    // Clear permissions for the well on unmount
    return () => dispatch({ type: UserActionType.CLEAR_USER_OBJECT_PERMISSIONS, payload: {} })
  }, [wellPermissions, dispatch])

  const workflowNavSteps = useMemo(() => {
    if (wellMetaData?.isDraft || !id || (!canEditWell(user) && wellMetaData?.isUnderReview)) {
      return getNewWellLinks({ id, mode, workflowState, wellMetaData, user, location: location.pathname })
    } else if (wellMetaData?.isUnderReview || wellMetaData?.isPublished) {
      const links = [{ links: getExistingWellLinks(user, id, mode, wellMetaData) }]
      if (wellMetaData.isUnderReview && can(user, [Permissions.REVIEW_WELL])) {
        links.push({ links: getWellReviewLinks(id) })
      }
      return links
    } else {
      return []
    }
  }, [workflowState, wellMetaData, user, id, mode, location.pathname])

  useEffect(() => {
    if (wellMetaData) {
      setPageState((prevPageState) => ({
        ...prevPageState,
        headerTabs: wellMetaData?.isDraft ? undefined : getHeaderTabs(id, mode, user),
      }))
      // Only show AGS upload link in Draft state
      setShouldShowAGSLink(!!wellMetaData?.isDraft)
    }
  }, [user, id, mode, setPageState, wellMetaData, setShouldShowAGSLink])

  useEffect(() => {
    if (isWorkflowStateSuccess) {
      setWorkflowState(workflowData?.state)
    }
  }, [isWorkflowStateSuccess, workflowData?.state])

  useEffect(() => {
    if (isWellMetaDataSuccess) {
      setWellMetaData(metadataData?.state)
    }
  }, [isWellMetaDataSuccess, metadataData?.state])

  // Trigger strata export when shouldExportStrata becomes true
  useEffect(() => {
    if (shouldExportStrata) {
      requestCsvExportUrl()
      setShouldExportStrata(false)
    }
  }, [shouldExportStrata, setShouldExportStrata, requestCsvExportUrl])

  return (
    <>
      <WellStateProvider wellState={wellMetaData ?? {}}>
        <WorkflowStateProvider navigationSteps={workflowNavSteps}>
          <Outlet />
          {/* AGS Modal lives here as it is shared by all new well routes */}
          <AGSUploadModal
            isOpen={isAGSUploadModalOpen}
            setIsOpen={setIsAGSUploadModalOpen}
            contentLabel='Upload an AGS file'
          />
        </WorkflowStateProvider>
      </WellStateProvider>
      <DeleteWellModal isOpen={isDeleteWellModalOpen} setIsOpen={setIsDeleteWellModalOpen} wellId={id} />
    </>
  )
}
